import { useState } from 'react';

/**
 * FWI heatmap colour legend (Phase E.2.2.5).
 *
 * A small, dismissable fixed-position overlay shown while the Fire Weather Index
 * heatmap is active. The swatches reproduce the CWFIS `cffdrs_fwi` style **exactly**
 * (pulled live from the WMS GetLegendGraphic in E.2.2.1 — see
 * docs/phase-e2-scope/e2.2-fwi-discovery.md) so the operator recognises the same
 * colours they see in their own CWFIS tools (defensive pattern E.2.2.7 #5).
 *
 * NOTE: this is the *real* government scale, which differs from the original
 * E.2.2 spec assumption — there is a blue 0-5 band and the top class is pure red
 * (no dark-red).
 */

/** The official CWFIS `cffdrs_fwi` interval colour ramp, low → extreme. */
export const FWI_LEGEND_STOPS: ReadonlyArray<{ label: string; band: string; hex: string }> = [
  { label: 'Very low', band: '0–5', hex: '#0000FF' },
  { label: 'Low', band: '5–10', hex: '#00E000' },
  { label: 'Moderate', band: '10–20', hex: '#FFFF00' },
  { label: 'High', band: '20–30', hex: '#E0A000' },
  { label: 'Extreme', band: '>30', hex: '#FF0000' },
];

export function FwiLegend(): JSX.Element | null {
  const [dismissed, setDismissed] = useState(false);
  if (dismissed) return null;

  return (
    <div
      data-testid="fwi-legend"
      // Anchored to the MAP REGION's bottom-left (council Part IV). The component
      // renders inside the relative map-area wrapper, so `absolute left-3` sits
      // clear of the left rail instead of buried under it (was `fixed left-56`).
      className="absolute bottom-4 left-3 z-20 w-44 rounded-md border border-slate-700 bg-slate-900/90 text-slate-100 shadow-lg backdrop-blur-sm"
    >
      <div className="flex items-center gap-1 border-b border-slate-700/70 px-2 py-1.5">
        <span className="flex-1 text-[11px] font-semibold uppercase tracking-wider text-orange-300/90">
          Fire Weather Index
        </span>
        <button
          type="button"
          aria-label="Dismiss FWI legend"
          onClick={() => setDismissed(true)}
          className="rounded px-1 text-slate-400 hover:bg-slate-800 hover:text-slate-200"
        >
          ×
        </button>
      </div>
      <ul className="px-2 py-1.5 space-y-0.5">
        {FWI_LEGEND_STOPS.map((s) => (
          <li key={s.band} className="flex items-center gap-2 text-[11px]">
            <span
              className="inline-block h-3 w-3 rounded-sm border border-black/30"
              style={{ backgroundColor: s.hex }}
              aria-hidden
            />
            <span className="tabular-nums text-slate-300 w-10">{s.band}</span>
            <span className="flex-1 text-slate-400">{s.label}</span>
          </li>
        ))}
      </ul>
      <div className="px-2 pb-1.5 text-[9px] italic text-slate-500">CWFIS · cffdrs_fwi</div>
    </div>
  );
}
